import apiClient from "./api";
import {
  Planilha,
  CreatePlanilhaDTO,
  UpdatePlanilhaDTO,
  PaginationParams,
  PlanilhaStatus,
  ApiResponse,
  PaginatedResponse,
} from "../types";
import { normalizarStatusPlanilha } from "../utils/planilhaStatus";

export interface DuplicarPlanilhaDTO {
  clienteId: string;
  evento: string;
  dataInicio: string;
  dataFim?: string | null;
  localEvento?: string | null;
  ufEvento?: string | null;
  linkProjeto?: string | null;
}

export interface PlanilhaDuplicada extends Planilha {
  origemPlanilhaId: string;
  itensCopiados: number;
  vinculosCopiados: number;
}

export interface LockInfo {
  bloqueada: boolean;
  bloqueadaPor?: string;
  expiraEm?: string;
  usuarioNome?: string;
}

export interface PlanilhaHistoricoItem {
  id: string;
  tipoAlteracao: string;
  dadosAnteriores?: string;
  dadosNovos?: string;
  observacoes?: string;
  ipAddress?: string;
  createdAt: string;
  usuario?: { id: string; nome: string; email: string };
  item?: { id: string; numero: string; item: string };
}

export interface AdminLog extends PlanilhaHistoricoItem {
  planilhaId: string;
  planilha?: { evento: string; clienteNome: string };
}

export interface PlanilhaDeletada {
  id: string;
  evento: string;
  clienteNome: string;
  status: string;
  createdAt: string;
  deletedAt: string;
  deletedBy?: string;
  createdBy?: string;
}

/** Fornecedor de uma linha cuja cotação será invalidada por uma alteração. */
export interface FornecedorAfetado {
  fornecedorId: string;
  fornecedorNome: string;
  status: string;
  /** Valor já cotado que será perdido. Ausente se ainda não respondeu. */
  valorCotado?: number | null;
  /** Se existe documento assinado que será apagado. */
  temDocumento?: boolean;
}

export interface ItemAfetado {
  itemId: string;
  numero: string;
  item: string;
  camposAlterados: string[];
  fornecedoresAfetados: FornecedorAfetado[];
}

export interface VerificacaoAlteracoes {
  requiresConfirmation: boolean;
  itensAfetados: ItemAfetado[];
  mensagem: string;
}

export const planilhaService = {
  async getAll(
    params?: PaginationParams,
  ): Promise<PaginatedResponse<Planilha>> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.pageSize)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);

    const response = await apiClient.get<
      ApiResponse<PaginatedResponse<Planilha>>
    >(`/planilhas?${queryParams.toString()}`);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao buscar planilhas");
    }

    return response.data.data;
  },

  async getById(id: string): Promise<Planilha> {
    const response = await apiClient.get<ApiResponse<Planilha>>(
      `/planilhas/${id}`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Planilha não encontrada");
    }

    return response.data.data;
  },

  async getPlanilhaComFornecedoresCompletos(id: string): Promise<Planilha> {
    const response = await apiClient.get<ApiResponse<Planilha>>(
      `/planilhas/${id}/fornecedores-detalhe`,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Planilha não encontrada");
    }

    return response.data.data;
  },

  async create(planilha: CreatePlanilhaDTO): Promise<Planilha> {
    const response = await apiClient.post<ApiResponse<Planilha>>(
      "/planilhas",
      planilha,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao criar planilha");
    }

    return response.data.data;
  },

  async update(id: string, planilha: UpdatePlanilhaDTO): Promise<Planilha> {
    const response = await apiClient.put<ApiResponse<Planilha>>(
      `/planilhas/${id}`,
      planilha,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao atualizar planilha");
    }

    return response.data.data;
  },

  /**
   * Cria uma planilha nova a partir de outra, copiando itens e os fornecedores
   * vinculados a cada um. Valores, orçamentos, propostas e os dados do Wrike
   * (OC, link, tarefas) não vêm — a cópia nasce pronta para pedir cotação.
   */
  async duplicar(
    id: string,
    dados: DuplicarPlanilhaDTO,
  ): Promise<PlanilhaDuplicada> {
    const response = await apiClient.post<ApiResponse<PlanilhaDuplicada>>(
      `/planilhas/${id}/duplicar`,
      dados,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao duplicar a planilha");
    }

    return response.data.data;
  },

  /**
   * Altera só o status. Endpoint próprio porque o `update` manda a planilha
   * inteira: uma tela que quer mexer no status não deveria arriscar reescrever
   * itens, e o gatilho automático precisa da regra de "só avança", que vive no
   * servidor.
   *
   * `automatico` marca a chamada como gatilho (FACITE gerado, envio ao Wrike);
   * nesse modo o servidor ignora em silêncio o que seria retrocesso e devolve
   * o status que ficou valendo. Sem a flag, é escolha da pessoa e vale sempre.
   */
  async atualizarStatus(
    id: string,
    status: PlanilhaStatus,
    opcoes?: { automatico?: boolean },
  ): Promise<{ status: PlanilhaStatus; alterado: boolean }> {
    const response = await apiClient.patch<
      ApiResponse<{ status: string; alterado: boolean }>
    >(`/planilhas/${id}/status`, {
      status,
      automatico: opcoes?.automatico ?? false,
    });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao atualizar o status");
    }

    return {
      status: normalizarStatusPlanilha(response.data.data.status),
      alterado: response.data.data.alterado,
    };
  },

  async verificarAlteracoes(
    id: string,
    planilha: UpdatePlanilhaDTO,
  ): Promise<VerificacaoAlteracoes> {
    const response = await apiClient.post<ApiResponse<VerificacaoAlteracoes>>(
      `/planilhas/${id}/verificar-alteracoes`,
      planilha,
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao verificar alterações");
    }

    return response.data.data;
  },

  async delete(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/planilhas/${id}`,
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Erro ao deletar planilha");
    }
  },

  async exportFacite(id: string): Promise<void> {
    try {
      const response = await apiClient.post(
        `/planilhas/${id}/export-facite`,
        {},
        {
          responseType: "blob",
        },
      );

      // Criar download do arquivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `FACITE_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.warn("API não disponível, simulando export");
      alert("Funcionalidade de exportação FACITE será implementada");
    }
  },

  async importExcel(file: File): Promise<Planilha> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post<ApiResponse<Planilha>>(
        "/planilhas/import",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || "Erro ao importar planilha");
      }

      return response.data.data;
    } catch (error) {
      console.warn("API não disponível, simulando import");
      throw new Error("Funcionalidade de importação será implementada");
    }
  },

  // ==================== LOCK ====================

  async acquireLock(id: string): Promise<LockInfo> {
    try {
      const response = await apiClient.post<ApiResponse<LockInfo>>(
        `/planilhas/${id}/lock`,
      );
      return response.data.data ?? { bloqueada: false };
    } catch (err: any) {
      if (err.response?.status === 423) {
        return err.response.data as LockInfo;
      }
      throw err;
    }
  },

  async releaseLock(id: string): Promise<void> {
    try {
      await apiClient.delete(`/planilhas/${id}/lock`);
    } catch {
      // Ignorar erros ao liberar lock — pode ter expirado
    }
  },

  async heartbeatLock(id: string): Promise<void> {
    try {
      await apiClient.put(`/planilhas/${id}/lock/heartbeat`);
    } catch {
      // Ignorar erros de heartbeat
    }
  },

  async getLockStatus(id: string): Promise<LockInfo> {
    const response = await apiClient.get<ApiResponse<LockInfo>>(
      `/planilhas/${id}/lock`,
    );
    return response.data.data ?? { bloqueada: false };
  },

  // ==================== HISTÓRICO ====================

  async getHistorico(
    id: string,
    page = 1,
    pageSize = 30,
  ): Promise<PaginatedResponse<PlanilhaHistoricoItem>> {
    const response = await apiClient.get<
      ApiResponse<PaginatedResponse<PlanilhaHistoricoItem>>
    >(`/planilhas/${id}/historico?page=${page}&pageSize=${pageSize}`);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao buscar histórico");
    }
    return response.data.data;
  },

  // ==================== ADMIN ====================

  async getDeletedPlanilhas(
    params?: PaginationParams,
  ): Promise<PaginatedResponse<PlanilhaDeletada>> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append("page", params.page.toString());
    if (params?.pageSize) qs.append("pageSize", params.pageSize.toString());
    if (params?.search) qs.append("search", params.search);

    const response = await apiClient.get<
      ApiResponse<PaginatedResponse<PlanilhaDeletada>>
    >(`/painel/planilhas/deletadas?${qs.toString()}`);

    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.message || "Erro ao buscar planilhas deletadas",
      );
    }
    return response.data.data;
  },

  async getAdminHistorico(
    id: string,
    page = 1,
    pageSize = 50,
  ): Promise<PaginatedResponse<PlanilhaHistoricoItem>> {
    const response = await apiClient.get<
      ApiResponse<PaginatedResponse<PlanilhaHistoricoItem>>
    >(`/painel/planilhas/${id}/historico?page=${page}&pageSize=${pageSize}`);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao buscar histórico");
    }
    return response.data.data;
  },

  async getAdminLogs(params: {
    page?: number;
    pageSize?: number;
    planilhaId?: string;
    usuarioId?: string;
    tipoAcao?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<PaginatedResponse<AdminLog>> {
    const qs = new URLSearchParams();
    if (params.page) qs.append("page", params.page.toString());
    if (params.pageSize) qs.append("pageSize", params.pageSize.toString());
    if (params.planilhaId) qs.append("planilhaId", params.planilhaId);
    if (params.usuarioId) qs.append("usuarioId", params.usuarioId);
    if (params.tipoAcao) qs.append("tipoAcao", params.tipoAcao);
    if (params.dateFrom) qs.append("dateFrom", params.dateFrom);
    if (params.dateTo) qs.append("dateTo", params.dateTo);

    const response = await apiClient.get<
      ApiResponse<PaginatedResponse<AdminLog>>
    >(`/painel/logs?${qs.toString()}`);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Erro ao buscar logs");
    }
    return response.data.data;
  },

  async getAdminLocks(): Promise<
    {
      planilhaId: string;
      usuarioNome: string;
      adquiridoEm: string;
      expiraEm: string;
    }[]
  > {
    const response = await apiClient.get<ApiResponse<any[]>>("/painel/locks");
    return response.data.data ?? [];
  },

  async forceUnlock(planilhaId: string): Promise<void> {
    await apiClient.delete(`/painel/planilhas/${planilhaId}/lock`);
  },
};
